import * as Yup from 'yup';
import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { Box, Button, Link, Stack, Alert, IconButton, InputAdornment, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useTheme } from '@mui/material/styles';
// routes
import { PATH_AUTH } from '../../../routes/paths';
// hooks
import useAuth from '../../../hooks/useAuth';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
// components
import Iconify from '../../../components/Iconify';
import { FormProvider, RHFTextField, RHFCheckbox } from '../../../components/hook-form';

import * as api from '../../../_apis_/service';

import OTPInput, { ResendOTP } from "otp-input-react";

// ----------------------------------------------------------------------

export default function LoginForm() {
  const { login } = useAuth();
  const theme = useTheme();

  const isMountedRef = useIsMountedRef();

  const [isLoading, setIsLoading] = useState(false);
  const [OTP, setOTP] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [resetPassword, setResetPassword] = useState(false);
  const [otpRequested, setOtpRequested] = useState(false);
  const [otpIncorrect, setOtpIncorrect] = useState(false);
  const [otpVerified, setOtpVerified] = useState(null);
  const [secondsLeftReOtp, setSecondsLeftReOtp] = useState(30);

  const sessionExpired = window.localStorage.getItem('sessionExpired')

  const renderButton = (buttonProps) => {
    return (
      <Box sx={{width: 1, display: 'flex', justifyContent: 'center'}}>
      {buttonProps?.remainingTime !== 0 ?
        <Button disabled>You can request OTP in {buttonProps?.remainingTime} seconds</Button>
        : <Button variant="outlined" onClick={() => resendOtp()}>Resend OTP</Button>
      }
      </Box>
    );
  };
  const renderTime = () => React.Fragment;

  const validateEmail = (email: string | undefined) => {
     return Yup.string().email().isValidSync(email)
  };

  const validatePhone = (phone: number | undefined) => {
     return Yup.number().integer().positive().test(
        (phone) => {
          return (phone && phone.toString().length == 10) ? true : false;
        }
      ).isValidSync(phone);
  };

  const LoginSchema = Yup.object().shape({
    email_or_phone: Yup.string()
      .required('Email / Phone is required')
      .test('email_or_phone', 'Email / Phone is invalid', (value) => {
         return validateEmail(value) || validatePhone(parseInt(value ?? '0'));
      })
  });

  const defaultValues = {
    email_or_phone: '',
    email: null,
    mobile: null,
    password: '',
    loginWithOtp: true,
    remember: true,
    loginResponse: null
  };

  const methods = useForm({
    resolver: yupResolver(LoginSchema),
    defaultValues,
  });

  const {
    reset,
    watch,
    setError,
    setValue,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = methods;

  const values = watch();

  // Line Commenting for better readability

  const onSubmit = async (data) => {
    try {
      console.log('Login button clicked', values.loginWithOtp)
      if (values.loginWithOtp && !otpRequested) {
        await api.requestOtp(data.mobile, data.email).then(res=>{
          setOtpRequested(true);
        }).catch(err=>console.log(err))
      } else {
        let loginResponse = await login(data.mobile, data.email, data.password, OTP); 
        if (loginResponse) {
          setValue('loginResponse',loginResponse)
        }
      }
    } catch (error) {
      console.error(error);
      reset();
      if (isMountedRef.current) {
        setError('afterSubmit', error);
      }
    }
  };

  const resendOtp = async () => {
    await setIsLoading(true);
    await setOtpVerified(null);
    await setOtpIncorrect(false);
    await setValue('loginResponse', null);
    await api.resendOtp(values.mobile, values.email).then(res=>{
      setOtpRequested(true);
      setSecondsLeftReOtp(30);
    }).catch(err=>console.log(err))
    setIsLoading(false);
  };

  useEffect(() => {
    if (values.email_or_phone) {
      if (Yup.string().email().isValidSync(values.email_or_phone)) {
        setValue('email', values.email_or_phone)
        setValue('mobile', null)
      } else if (Yup.number().integer().positive().isValidSync(values.email_or_phone)) {
        setValue('mobile', values.email_or_phone)
        setValue('email', null)
      } else {
        setValue('mobile', null)
        setValue('email', null)
      }
    }
  }, [values.email_or_phone]);

  useEffect(() => {
    if (localStorage.getItem('passwordReset')) {
      localStorage.removeItem('passwordReset');
      setResetPassword(true);
    }
  }, []);

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        {!otpRequested && !errors?.afterSubmit?.message && !sessionExpired && !values?.loginResponse && !resetPassword &&
          <Alert severity="info" sx={{ mb: 1, fontSize: '0.8em' }}>
            Please reach out to your Partner Success Manager in case of any queries or challenges, or send your query on <Link underline="always" color="text.primary" href="mailto:unity@brightpay.in">unity@brightpay.in</Link>
          </Alert>
        }

        {otpRequested && !errors?.afterSubmit?.message && sessionExpired && !values?.loginResponse && !resetPassword &&
          <Alert severity="error" sx={{ mb: 1 }}>
            Session Expired. Please re-login!
          </Alert>
        }
        {otpRequested && resetPassword &&
          <Alert severity="success" sx={{ mb: 1 }}>
            You've succesfully reset your password! Please re-login with your new credentials
          </Alert>
        }
        {values?.loginResponse && <Alert severity="error">{values.loginResponse}</Alert>}
        {errors?.afterSubmit?.message && <Alert severity="error">{errors.afterSubmit.message}</Alert>}

        {!otpRequested && 
          <RHFTextField name="email_or_phone" label="Email address or Mobile" />
        }

        {!values.loginWithOtp &&
          <RHFTextField
            name="password"
            label="Password"
            disabled={values.loginWithOtp}
            type={showPassword ? 'text' : 'password'}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                    <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        }
      </Stack>

      { (otpRequested || otpIncorrect) && !isLoading &&
        <Box>
          {otpIncorrect &&
            <Typography
              variant="body1"
              color="error"
              align="center"
              fontWeight={500}
              gutterBottom
              sx={{
                marginBottom: 3
              }}
            >
              Incorrect OTP
            </Typography>
          }
          <Typography
            variant="h5"
            align="center"
            color="text.primary"
            fontWeight={500}
            sx={{
              marginY: 2
            }}
            gutterBottom
          >
            Enter the OTP sent to {values.email_or_phone}
          </Typography>
          <Box display={'flex'} justifyContent={'center'} alignItems={'center'}>
            <OTPInput
              value={OTP}
              onChange={setOTP}
              autoFocus
              OTPLength={4}
              otpType="number"
              disabled={false}
              inputStyles={{
                height: 64,
                width: 64,
                fontSize: 30,
                borderRadius: 12,
                border: `2px solid ${theme.palette.primary.main}`,
                color: theme.palette.secondary.main,
                marginBottom: '24px'
              }}
            />
          </Box>
        </Box>
      }

      {!otpRequested &&
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ my: 2 }}>
          {false &&
            <RHFCheckbox name="remember" label="Remember me?" />
          }
          {true &&
            <RHFCheckbox name="loginWithOtp" label="Login with OTP" />
          }
          <Link component={RouterLink} variant="subtitle2" to={PATH_AUTH.forgotPassword}>
            Forgot password?
          </Link>
        </Stack>
      }

      <LoadingButton fullWidth size="large" type="submit" variant="contained" loading={isSubmitting}>
        {(values.loginWithOtp && !otpRequested) ? 'Request OTP' : 'Login'}
      </LoadingButton>
      <Typography variant="body2" color="primary" sx={{ mt: 1, textAlign: 'right'}}>By logging in, you accept <a href="/terms-and-conditions" target="_blank">Terms & Conditions</a></Typography>
      {(otpRequested || otpIncorrect) && !isLoading &&
        <Box marginTop={3}>
          <ResendOTP
            maxTime={secondsLeftReOtp}
            renderButton={renderButton}
            renderTime={renderTime}
          />
        </Box>
      }
    </FormProvider>
  );
}
